import React from "react";
import { Button, Space, Typography } from "antd";
import { LinkOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { LEANCUSTOMS_ORDER_BASE } from "../../constants/leancustoms";
import type { LeanCustomsOrderDetailsDisplay } from "../../utils/leanCustomsOrderDetails";
import { formatByteSize } from "../../utils/leanCustomsRecordUtils";

export interface BookingCustomsLinkedOrderPanelProps {
  orderId: string;
  orderError: string | null;
  orderDetailLine: string | null;
  orderUpdatedAt: string | null;
  details: LeanCustomsOrderDetailsDisplay | null;
}

function DetailRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  if (value == null || value === "" || value === false) return null;
  return (
    <Typography.Text type="secondary" style={{ fontSize: 12, display: "block" }}>
      <Typography.Text strong style={{ fontSize: 12 }}>
        {label}:
      </Typography.Text>{" "}
      {value}
    </Typography.Text>
  );
}

export const BookingCustomsLinkedOrderPanel: React.FC<
  BookingCustomsLinkedOrderPanelProps
> = ({ orderId, orderError, orderDetailLine, orderUpdatedAt, details }) => {
  return (
    <Space direction="vertical" size={4} style={{ width: "100%" }}>
      <Space wrap>
        <Typography.Text type="success" strong>
          {orderId}
        </Typography.Text>
        <Button
          type="link"
          size="small"
          icon={<LinkOutlined />}
          href={`${LEANCUSTOMS_ORDER_BASE}/${orderId}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open in Lean Customs
        </Button>
      </Space>
      {orderError ? (
        <Typography.Text type="warning" style={{ fontSize: 12 }}>
          {orderError}
        </Typography.Text>
      ) : orderDetailLine ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {orderDetailLine}
        </Typography.Text>
      ) : (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Order details not available from Lean Customs.
        </Typography.Text>
      )}
      {orderUpdatedAt ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Updated {dayjs(orderUpdatedAt).format("YYYY-MM-DD HH:mm")}
        </Typography.Text>
      ) : null}

      {details ? (
        <div style={{ marginTop: 4 }}>
          <DetailRow label="Declarant" value={details.declarant} />
          <DetailRow label="Importer" value={details.importer} />
          <DetailRow label="Agent" value={details.agent} />
          {details.shipmentReferences.length > 0 ? (
            <DetailRow
              label="Shipment reference"
              value={details.shipmentReferences.join(" · ")}
            />
          ) : null}
          {details.costLines.length > 0 ? (
            <Typography.Text
              type="secondary"
              style={{ fontSize: 12, display: "block", marginTop: 4 }}
            >
              <Typography.Text strong style={{ fontSize: 12 }}>
                Cost lines:
              </Typography.Text>
              <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                {details.costLines.map((line) => (
                  <li key={`${line.label}-${line.amount}`}>
                    {line.label}: {line.amount}
                  </li>
                ))}
              </ul>
            </Typography.Text>
          ) : null}
          {details.documents.length > 0 ? (
            <Typography.Text
              type="secondary"
              style={{ fontSize: 12, display: "block", marginTop: 4 }}
            >
              <Typography.Text strong style={{ fontSize: 12 }}>
                Order documents:
              </Typography.Text>
              <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                {details.documents.map((doc) => (
                  <li key={doc.filename}>
                    {doc.filename}
                    {doc.documentType ? ` (${doc.documentType})` : ""}
                    {doc.byteSize != null ? ` · ${formatByteSize(doc.byteSize)}` : ""}
                  </li>
                ))}
              </ul>
            </Typography.Text>
          ) : null}
        </div>
      ) : null}
    </Space>
  );
};
